import DataLoader from "dataloader";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getVariantImages } from "@/lib/api";

const MAX_IMAGE_BATCH_SIZE = 100;

// Coalesces same-tick `.load(id)` calls from many components (product cards
// across a page, the detail page, the variant editor, ...) into the single
// batched `/images` request each page used to have to wire up by hand. Keep
// each batch within the backend endpoint's 100-image limit.
const loader = new DataLoader<number, string | null>(async ids => {
  const urls = await getVariantImages([...ids]);
  return ids.map(id => urls[id] ?? null);
}, { maxBatchSize: MAX_IMAGE_BATCH_SIZE });

const variantImageKey = (id: number) => ["variantImage", id] as const;

// Variant image URLs are permanent public Supabase Storage URLs - the same
// picture always resolves to the same URL until it's replaced, at which
// point uploadVariantImage's caller updates the cache directly (see
// useSetVariantImage below) - so a resolved entry never needs a refetch.
const IMAGE_STALE_TIME = Infinity;

// undefined while resolving, null once resolved with no image - the
// convention ProductImage/ProductCard already render against. Because the
// query key is shared across every page, a variant whose picture was
// already resolved on the home page or product list renders instantly here
// with no extra request.
export function useVariantImage(id: number | undefined): string | null | undefined {
  const { data } = useQuery({
    queryKey: variantImageKey(id ?? -1),
    queryFn: () => loader.load(id!),
    enabled: id != null,
    staleTime: IMAGE_STALE_TIME,
  });
  return data;
}

export function useVariantImagesMap(ids: number[]): Record<number, string | null | undefined> {
  const results = useQueries({
    queries: ids.map(id => ({
      queryKey: variantImageKey(id),
      queryFn: () => loader.load(id),
      staleTime: IMAGE_STALE_TIME,
    })),
  });

  const map: Record<number, string | null | undefined> = {};
  ids.forEach((id, i) => {
    map[id] = results[i]?.data;
  });
  return map;
}

// Keeps the shared cache correct after an upload - both the dataloader
// (which would otherwise keep serving its pre-upload cached promise to any
// new subscriber) and react-query's cache (so components already mounted
// pick up the new picture immediately).
export function useSetVariantImage() {
  const queryClient = useQueryClient();
  return (id: number, url: string | null) => {
    loader.clear(id).prime(id, url);
    queryClient.setQueryData(variantImageKey(id), url);
  };
}
